import { useState, useRef, useEffect } from 'react';

const contactInfo = [
  {
    title: 'Address Details',
    icon: 'ri-map-pin-line',
    items: [
      { label: 'Address', value: 'STR. CRANGULUI NR. 27 BL. 19 SC. A AP. 9' },
    ],
  },
  {
    title: 'Location',
    icon: 'ri-global-line',
    items: [
      { label: 'City', value: 'Sfantu Gheorghe' },
      { label: 'Zip', value: '520042' },
      { label: 'Country', value: 'Romania' },
    ],
  },
  {
    title: 'Electronic Contact',
    icon: 'ri-mail-line',
    items: [
      { label: 'Email', value: 'help@meltisrl.com' },
    ],
  },
  {
    title: 'Direct Line',
    icon: 'ri-phone-line',
    items: [
      { label: 'Number', value: '+1 7406758689' },
    ],
  },
];

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setFormStatus('sending');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      const res = await fetch('https://readdy.ai/api/form/d7f2ujuivmjfhtdrfn7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="w-full bg-gray-50 py-24 md:py-36 px-6 md:px-10"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-4">Get In Touch</p>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 900,
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              lineHeight: 1.1,
              color: '#111',
            }}
          >
            Contact Us
          </h2>
        </div>

        {/* Contact Info Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {contactInfo.map((col) => (
            <div key={col.title} className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${col.icon} text-gray-400 text-base`}></i>
                </div>
                <span className="text-gray-400 text-xs tracking-[0.2em] uppercase font-500" style={{ fontWeight: 500 }}>
                  {col.title}
                </span>
              </div>
              <div className="h-px bg-gray-200 mb-4"></div>
              <div className="flex flex-col gap-2">
                {col.items.map((item) => (
                  <div key={item.label} className="flex flex-col">
                    <span className="text-gray-400 text-xs">{item.label}:</span>
                    <strong className="text-gray-800 text-sm font-600 mt-0.5" style={{ fontWeight: 600 }}>
                      {item.value}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Form */}
        <div
          className={`bg-white rounded-2xl p-8 md:p-12 max-w-2xl transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3
            className="text-gray-900 mb-2 text-xl md:text-2xl"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
          >
            Send a Message
          </h3>
          <p className="text-gray-400 text-sm mb-8 font-300" style={{ fontWeight: 300 }}>
            Have a question or inquiry? We&apos;d love to hear from you.
          </p>

          {formStatus === 'success' ? (
            <div className="flex flex-col items-center py-12 gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-50">
                <i className="ri-check-line text-green-500 text-2xl"></i>
              </div>
              <p className="text-gray-700 font-500 text-base" style={{ fontWeight: 500 }}>
                Message sent successfully!
              </p>
              <p className="text-gray-400 text-sm text-center">
                Thank you for reaching out. We&apos;ll get back to you shortly.
              </p>
              <button
                onClick={() => setFormStatus('idle')}
                className="mt-4 text-sm text-gray-500 underline cursor-pointer"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-gray-600 text-xs font-500 uppercase tracking-wider" style={{ fontWeight: 500 }}>
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none focus:border-gray-400 transition-colors bg-gray-50"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-gray-600 text-xs font-500 uppercase tracking-wider" style={{ fontWeight: 500 }}>
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none focus:border-gray-400 transition-colors bg-gray-50"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-gray-600 text-xs font-500 uppercase tracking-wider" style={{ fontWeight: 500 }}>
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  className="border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 outline-none focus:border-gray-400 transition-colors bg-gray-50 resize-none"
                />
                <span className="text-gray-400 text-xs text-right">{formData.message.length}/500</span>
              </div>

              {formStatus === 'error' && (
                <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={formStatus === 'sending' || formData.message.length > 500}
                className="bg-gray-900 text-white rounded-lg px-8 py-3.5 text-sm font-500 cursor-pointer hover:bg-gray-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 500 }}
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
